import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IEventoGenerico, IFilter, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IRegExpSearch,
  IFiltroTabla,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../../auxiliares/tabla/tabla.component';
import { LoginService } from '../../../../login/login.service';
import { TratamientoGenericoComponent } from '../../../eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { estadoTecnicoCelda } from '../estado-tecnico.util';

@Component({
  selector: 'app-vista-tecnicos-genericos',
  standalone: false,
  templateUrl: './vista-tecnicos.component.html',
  styleUrl: './vista-tecnicos.component.scss',
})
export class VistaTecnicosGenericosComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private dialog = inject(MatDialog);

  private usuarioPropio = LoginService.getUsuario();
  //// Tabla nueva
  public name = 'VistaTecnicosComponent';
  public datos: IEventoGenerico[] = [];
  public datos$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IEventoGenerico>[];

  public search: IRegExpSearch = {
    fields: ['valores.titulo'],
  };
  // FILTROS de cliente. Propietario = coincidencia exacta; jerarquía = ese
  // cliente y toda su descendencia (idsAncestros no incluye al propio).
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];

  public filter: IFilter<IEventoGenerico> = {
    'detallesTecnicos.idTecnicoAsignado': this.usuarioPropio._id,
    estado: {
      $in: ['Asignado', 'En Atención', 'Pendiente de Aprobación'],
    },
    filtrador: 'Servicio Técnico',
  } as any;
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'activo',
      select: 'identificacion',
    },
    {
      path: 'alarma',
      select: 'nombre idDomicilio',
      populate: {
        path: 'domicilio',
        select: 'geojson',
      },
    },
    {
      path: 'tracker',
      select: 'identificacion',
    },
    {
      path: 'luminaria',
      select: 'identificacion',
    },
    {
      path: 'gateway',
      select: 'nombre',
    },
    {
      path: 'detallesTecnicos.tecnico',
      select: 'usuario datosPersonales',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  public async listar(): Promise<void> {
    // El filtro fijo (técnico propio, estados) se suma al que arma la tabla:
    // la búsqueda y los filtros reemplazan query.filter y sin esto se perdía.
    const filter: Record<string, unknown> = HelperService.stringToObject(
      this.query.filter,
    );
    Object.assign(filter, this.filter);
    const query: IQueryParam = {
      ...this.query,
      filter: JSON.stringify(filter),
    };
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2.eventosGenericos(query, 30).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.datos = data.datos;
    });
  }

  // Acciones
  public async atender(data: IEventoGenerico) {
    this.dialog.open(TratamientoGenericoComponent, {
      data: { idEvento: data._id },
      hasBackdrop: false,
      minWidth: '800px',
      maxWidth: '1000px',
    });
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IEventoGenerico>[] = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          hace: true,
        },
      },
      // Estado
      {
        header: {
          label: 'Estado',
          sortable: true,
          sort: 'estado',
        },
        row: {
          estado: (dato) => estadoTecnicoCelda(dato),
          noData: 'Sin estado',
        },
      },
      // Título
      {
        header: {
          label: 'Título',
          sortable: true,
          sort: 'valores.titulo',
        },
        row: {
          parse(dato) {
            return dato.valores?.titulo;
          },
          noData: 'Sin título',
        },
      },
      // Categoría
      {
        header: {
          label: 'Categoría',
        },
        row: {
          parse(dato) {
            return (dato.valores as any)?.categoria;
          },
          noData: 'Sin categoría',
        },
      },
      // Tracker o Alarma o Activo o Luminaria
      {
        header: {
          label: 'Elemento',
        },
        row: {
          parse(dato) {
            if (dato.tracker) {
              return dato.tracker.identificacion;
            }
            if (dato.alarma) {
              return dato.alarma.nombre;
            }
            if (dato.activo) {
              return dato.activo.identificacion;
            }
            if (dato.luminaria) {
              return dato.luminaria.identificacion;
            }
            if (dato.gateway) {
              return dato.gateway.nombre;
            }
            return undefined;
          },
          noData: 'Sin elemento',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'troubleshoot',
            tooltip: 'Atender',
            click: (dato) => this.atender(dato),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.datos$?.unsubscribe();
  }
}
